import React, { useEffect, useState } from 'react';
import './TeeSelectionModal.css';
import { getTeeColor, teeNeedsOutline } from '../utils/teeColors';

/**
 * Which tee(s) a round is played from.
 *
 * A society playing a mixed-sex or mixed-ability day uses several tees at once,
 * so this selects a SET. The set is the menu the per-player pickers offer later
 * (Assign Groups); the "main" tee within it is the one anybody unassigned plays
 * from, and is what every event that uses a single tee resolves to.
 *
 * onSelect(tee, teesInPlay) — `tee` is the main tee (null = the course's own
 * card), `teesInPlay` the full list of tee ids declared.
 */
const TeeSelectionModal = ({ isOpen, onSelect, onClose, course }) => {
  const [selectedIds, setSelectedIds] = useState([]);

  // Reopening for a different course must not carry the previous course's
  // selection over.
  useEffect(() => {
    if (isOpen) setSelectedIds([]);
  }, [isOpen, course?.id]);

  if (!isOpen || !course) return null;

  const tees = course.tees || [];
  // First picked is the main tee — the order the organiser chose in, which is
  // the least surprising rule and needs no extra control for the common
  // single-tee case.
  const mainTeeId = selectedIds[0] ?? null;
  const multiple = selectedIds.length > 1;

  const toggle = (teeId) => {
    setSelectedIds(prev => prev.includes(teeId)
      ? prev.filter(id => id !== teeId)
      : [...prev, teeId]);
  };

  const confirm = () => {
    const main = tees.find(t => t.id === mainTeeId) || null;
    onSelect(main, selectedIds);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content tee-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Select Tees</h2>
          <p>
            Which tees will be played at <strong>{course.name}</strong>?
            Pick more than one for a mixed group.
          </p>
        </div>
        <div className="tee-selection-list">
          {tees.map(tee => {
            const color = getTeeColor(tee.name);
            const checked = selectedIds.includes(tee.id);
            const isMain = multiple && tee.id === mainTeeId;
            return (
              <button
                key={tee.id}
                type="button"
                className={`tee-selection-btn${checked ? ' tee-selection-btn--checked' : ''}`}
                aria-pressed={checked}
                onClick={() => toggle(tee.id)}
              >
                <span className="tee-selection-btn__left">
                  <span className={`tee-check${checked ? ' tee-check--on' : ''}`} aria-hidden="true">
                    {checked ? '✓' : ''}
                  </span>
                  <span
                    className={`tee-color-dot${teeNeedsOutline(tee.name) ? ' tee-color-dot--white' : ''}`}
                    style={{ backgroundColor: color || '#ffffff' }}
                  />
                  <span className="tee-selection-btn__name">
                    {tee.name}
                    {isMain && <span className="tee-main-badge">Main</span>}
                  </span>
                </span>
                <span className="tee-selection-btn__ratings">CR {tee.course_rating ?? '-'} · SL {tee.slope_rating ?? '-'}</span>
              </button>
            );
          })}
          {/* The course's own card, offered ONLY when it has no tees of its
              own. Where real tees exist it is never the right answer — picking
              it would score the round off an unnamed card while the tees that
              were actually played sit right above it. */}
          {!tees.length && (
          <button
            type="button"
            className="tee-selection-btn tee-selection-btn--default"
            onClick={() => onSelect(null, [])}
          >
            <span className="tee-selection-btn__left">
              <span className="tee-check" aria-hidden="true" />
              <span className="tee-color-dot tee-color-dot--white" style={{ backgroundColor: '#ffffff' }} />
              <span className="tee-selection-btn__name">Default</span>
            </span>
            <span className="tee-selection-btn__ratings">CR {course.course_rating ?? '-'} · SL {course.slope_rating ?? '-'}</span>
          </button>
          )}
        </div>

        {multiple && (
          <p className="tee-selection-hint">
            {selectedIds.length} tees in play. Everyone plays{' '}
            <strong>{tees.find(t => t.id === mainTeeId)?.name}</strong> unless
            given another tee when you assign groups.
          </p>
        )}

        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
          <button
            className="confirm-button"
            disabled={!selectedIds.length}
            onClick={confirm}
          >
            {multiple ? `Use ${selectedIds.length} tees` : 'Use this tee'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeeSelectionModal;
